import { BlockNoteEditor, type PartialBlock } from '@blocknote/core';

import {
  flattenPartialBlocks,
  partialBlockToStored,
  type FlatBlock,
} from '@/components/editor/blocknote-types';
import { couplespaceTableEnterExtension } from '@/components/editor/couplespace-table-enter.web';
import { couplespaceSchema } from '@/components/editor/editor-schema.web';
import { repairPartialBlocks } from '@/components/editor/sanitize-initial-content';
import { newBlockId } from '@/lib/blocks/ids';

import { convertLocalFileLinksToFileBlocks } from './convert-file-links.js';
import { normalizeNotionMarkdownForImport } from './normalize-notion-markdown.js';
import { stripDuplicateLeadingH1 } from './strip-page-title.js';

export type ImportContentBlockPayload = {
  id: string;
  parentId: string;
  type: string;
  content: Record<string, unknown>;
  position: number;
};

export type NotionMarkdownConversion = {
  contentBlocks: ImportContentBlockPayload[];
  contentRootId: string;
};

type ImportParserEditor = BlockNoteEditor<
  typeof couplespaceSchema.blockSchema,
  typeof couplespaceSchema.inlineContentSchema,
  typeof couplespaceSchema.styleSchema
>;

/** Reused for batch import so we do not spin up a new editor per page. */
let importParserEditor: ImportParserEditor | null = null;

function getImportParserEditor(): ImportParserEditor {
  if (!importParserEditor) {
    importParserEditor = BlockNoteEditor.create({
      schema: couplespaceSchema,
      extensions: [couplespaceTableEnterExtension],
    });
  }
  return importParserEditor;
}

export { normalizeNotionMarkdownForImport } from './normalize-notion-markdown.js';

function assignBlockIds(blocks: PartialBlock[]): PartialBlock[] {
  return blocks.map((block) => ({
    ...block,
    id: block.id ?? newBlockId(),
    children: block.children?.length
      ? assignBlockIds(block.children as PartialBlock[])
      : block.children,
  }));
}

function flatToPayload(flat: FlatBlock[]): ImportContentBlockPayload[] {
  return flat.map((block) => {
    const stored = partialBlockToStored({
      type: block.type,
      props: block.content.props ?? {},
      content: block.content.content,
    } as PartialBlock);
    return {
      id: block.id,
      parentId: block.parentId,
      type: block.type,
      content: { props: stored.props ?? {}, content: stored.content },
      position: block.position,
    };
  });
}

export async function markdownToPartialBlocks(
  markdown: string,
  pageTitle = '',
): Promise<PartialBlock[]> {
  let trimmed = normalizeNotionMarkdownForImport(markdown).trim();
  if (pageTitle) {
    trimmed = stripDuplicateLeadingH1(trimmed, pageTitle);
  }
  if (!trimmed) {
    return [];
  }

  let partials: PartialBlock[];
  try {
    const editor = getImportParserEditor();
    partials = editor.tryParseMarkdownToBlocks(trimmed) as unknown as PartialBlock[];
    if (partials.length === 0) {
      partials = [
        {
          type: 'paragraph',
          content: [{ type: 'text', text: trimmed, styles: {} }],
        } as PartialBlock,
      ];
    }
  } catch {
    partials = [
      {
        type: 'paragraph',
        content: [{ type: 'text', text: trimmed, styles: {} }],
      } as PartialBlock,
    ];
  }

  partials = convertLocalFileLinksToFileBlocks(partials);
  const { blocks: repaired } = repairPartialBlocks(assignBlockIds(partials));
  return repaired;
}

export async function notionMarkdownToContentBlocks(
  markdown: string,
  pageId: string,
  pageTitle = '',
): Promise<NotionMarkdownConversion> {
  const contentRootId = pageId;
  const repaired = await markdownToPartialBlocks(markdown, pageTitle);
  if (repaired.length === 0) {
    return { contentBlocks: [], contentRootId };
  }

  const flat: FlatBlock[] = flattenPartialBlocks(contentRootId, repaired, new Map());

  return {
    contentBlocks: flatToPayload(flat),
    contentRootId,
  };
}
